import { useCallback, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { PageResponse } from '../types';

const DEFAULT_PAGE_SIZE = 30;

export function usePagedList<T extends { id: number }>(
  fetcher: (page: number, size: number) => Promise<PageResponse<T>>,
  queryKey: string,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const requestRef = useRef(0);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const activeQueryRef = useRef(queryKey);

  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((pageToLoad: number, mode: 'replace' | 'append') => {
    if (mode === 'append') {
      if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      requestRef.current += 1;
      loadingRef.current = true;
      loadingMoreRef.current = false;
      hasMoreRef.current = false;
      setLoading(true);
      setLoadingMore(false);
      setHasMore(false);
      setError(null);
    }

    const requestId = requestRef.current;

    fetcherRef.current(pageToLoad, pageSize)
      .then((data) => {
        if (requestId !== requestRef.current) return;
        pageRef.current = data.page;
        hasMoreRef.current = !data.last;
        setHasMore(!data.last);
        setTotal(data.totalElements);
        setItems((prev) => {
          if (mode === 'replace') return data.content;
          const seen = new Set(prev.map((item) => item.id));
          return [...prev, ...data.content.filter((item) => !seen.has(item.id))];
        });
        if (mode === 'replace') setEpoch((value) => value + 1);
      })
      .catch(() => {
        if (requestId !== requestRef.current) return;
        if (mode === 'replace') {
          setItems([]);
          setTotal(0);
          hasMoreRef.current = false;
          setHasMore(false);
          pageRef.current = 0;
          setError('Impossible de charger les données.');
        }
      })
      .finally(() => {
        if (requestId !== requestRef.current) return;
        loadingRef.current = false;
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      });
  }, [pageSize]);

  const refresh = useCallback(() => {
    load(0, 'replace');
  }, [load]);

  const loadMore = useCallback(() => {
    load(pageRef.current + 1, 'append');
  }, [load]);

  const replaceItem = useCallback((id: number, item: T) => {
    setItems((prev) => prev.map((current) => (current.id === id ? item : current)));
  }, []);

  useFocusEffect(useCallback(() => {
    if (activeQueryRef.current !== queryKey) {
      activeQueryRef.current = queryKey;
      setItems([]);
      setTotal(0);
      pageRef.current = 0;
      hasMoreRef.current = false;
      setHasMore(false);
    }
    load(0, 'replace');
  }, [load, queryKey]));

  return { items, total, loading, loadingMore, hasMore, refresh, loadMore, replaceItem, epoch, error };
}
